import {
  Box,
  Card,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import rocket from "../../../Assets/rocket.png";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { getAuthMarketingCards, resolveMarketingBadgeAsset } from "../../../config/sellerOrgConfig";
import { isMsmeBuild } from "../../../constants/partnerBrand";
import { useSellerOrgConfig } from "../../../Hooks/useSellerOrgConfig";
import MsmeAuthMarketingPanel from "./MsmeAuthMarketing";

// Move styled component outside to avoid recreation on every render
const ContainerContentLayout = styled(Container)`
  // margin: 0 auto;
  width: calc(100vw - 65vw);
`;

const ContentLayout = () => {
  const location = useLocation();
  const orgConfig = useSellerOrgConfig();
  const { branding } = orgConfig;
  const isRegistration = location.pathname === "/registration";
  const isDirectChannel = orgConfig.channel === "direct";
  const contents = getAuthMarketingCards(orgConfig, isRegistration);
  const badgeAsset = resolveMarketingBadgeAsset(branding);

  const userDeletionContents = [
    {
      title: "What we delete",
      descriptionOne:
        "Store Users: name, phone, email, device tokens/preferences",
      descriptionTwo:
        "Restaurant/Store Admins: name, phone, email; owner PAN/Aadhaar (if collected); store FSSAI no.; store address & geo; store contact details; banking details used for settlements (masked storage); and related profile data.",
    },
    {
      title: "What we may retain & why",
      descriptionOne:
        "Required records: We keep only the minimum necessary information for legal, tax, audit, fraud-prevention, and bookkeeping purposes (for example, invoices, settlement ledgers, and tax components like GST/TCS/TDS).",
      descriptionTwo:
        "Retention period: As required by applicable Indian laws/regulations (typically 6–8 years from the end of the financial year), or longer if legally required. After that, we erase or anonymize.",
      descriptionThree:
        "Backups: Deleted profiles are removed from production systems; backups are purged on schedule and aren’t used to restore deleted profiles unless legally required.",
    },
    {
      title: "Timing",
      descriptionOne:
        "Immediate: if there are no open orders, refunds, chargebacks, or pending settlements.",
      descriptionTwo:
        "Queued: if something is pending. We’ll delete automatically once resolved and notify you.",
    },
  ];

  const renderBoldColonText = (text: string) => {
    if (!text.includes(":")) return text; // no colon, return normal
    const [boldPart, ...rest] = text.split(":");
    const normalPart = rest.join(":");
    return (
      <>
        <Box
          component="span"
          sx={{ fontWeight: 700, color: "rgba(7, 7, 7, 0.5)" }}
        >
          {boldPart}:
        </Box>
        {normalPart}
      </>
    );
  };

  return (
    <ContainerContentLayout>
      {location.pathname === "/user-delete" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
            width: "100%",
          }}
        >
          <Grid container sx={{ maxWidth: "calc(100% - 20px)", mx: "0 auto" }}>
            {userDeletionContents.map((item, index) => {
              return (
                <Grid item xs={12} sm={12} key={index}>
                  <Card
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(25px)",
                      WebkitBackdropFilter: "blur(25px)",
                      p: "13px",
                      borderRadius: "10px",
                      border: 0,
                      mt: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        textWrap: "pretty",
                        fontWeight: 700,
                        fontSize: "clamp(14px, 2vw, 16px)",
                        lineHeight: "150%",
                        mt: 0.5,
                      }}
                    >
                      {item.title}
                    </Typography>
                    {item.descriptionOne && (
                      <Typography
                        sx={{
                          mt: { xs: 0.5, sm: 1 },
                          textWrap: "pretty",
                          fontSize: "clamp(12px, 1.5vw, 14px)",
                          fontWeight: 500,
                          lineHeight: "150%",
                          color: "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {renderBoldColonText(item.descriptionOne)}
                      </Typography>
                    )}
                    {item.descriptionTwo && (
                      <Typography
                        sx={{
                          mt: { xs: 0.5, sm: 1 },
                          textWrap: "pretty",
                          fontSize: "clamp(12px, 1.5vw, 14px)",
                          fontWeight: 500,
                          lineHeight: "150%",
                          color: "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {renderBoldColonText(item.descriptionTwo)}
                      </Typography>
                    )}
                    {item.descriptionThree && (
                      <Typography
                        sx={{
                          mt: { xs: 0.5, sm: 1 },
                          textWrap: "pretty",
                          fontSize: "clamp(12px, 1.5vw, 14px)",
                          fontWeight: 500,
                          lineHeight: "150%",
                          color: "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {renderBoldColonText(item.descriptionThree)}
                      </Typography>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : isMsmeBuild() ? (
        <MsmeAuthMarketingPanel />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={rocket}
              alt="rocket-grow"
              sx={{ width: 105, height: 85 }}
            />
            <Box sx={{ width: 404, height: 110, mb: "20px" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "24px",
                  textAlign: "center",
                  mb: "2px",
                }}
              >
                Grow Your Business Faster by Selling on
                <span>
                  <Box
                    component="img"
                    src={badgeAsset}
                    alt={branding.marketingNetworkBadge === "tazty" ? "Tazty" : "ONDC"}
                    sx={{
                      width: isDirectChannel ? 80 : { xs: 60, sm: 80, md: 100 },
                      objectFit: "contain",
                      height: isDirectChannel ? 40 : 22,
                      verticalAlign: "middle",
                      mb: "2px",
                      ml: 0.5,
                    }}
                  />
                </span>
              </Typography>

              <Typography
                gutterBottom
                sx={{
                  fontWeight: 500,
                  fontSize: "12px",
                  py: 1,
                  textAlign: "center",
                  color: "rgba(0, 0, 0, 0.4)",
                }}
              >
                {branding.marketingSubheadline}
              </Typography>
            </Box>

            <Grid container sx={{ maxWidth: 460, mx: "0 auto" }}>
              {contents.map((item, index) => {
                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        backdropFilter: "blur(25px)",
                        WebkitBackdropFilter: "blur(25px)",
                        p: "13px",
                        borderRadius: "10px",
                        border: 0,
                        height: isDirectChannel ? 100 : 84,
                        mr: { md: [0, 2].includes(index) ? "10px" : 0, xs: 0 },
                        mt: { md: [2, 3].includes(index) ? "10px" : 0, xs: 0 },
                        ml: { md: [1, 3].includes(index) ? "10px" : 0, xs: 0 },
                        mb: { md: [0, 1].includes(index) ? "10px" : 0, xs: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "12px",
                          mb: 0.5,
                          lineHeight: "150%",
                          letterSpacing: "0em",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "10px",
                          mb: 0.5,
                          lineHeight: "150%",
                          letterSpacing: "0em",
                          color: " rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </>
      )}
    </ContainerContentLayout>
  );
};

export default ContentLayout;
